import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { House, LockKey, CheckCircle, XCircle, ArrowLeft } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", email: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/password-reset/verify/${token}`);
        if (cancelled) return;
        setState(data.valid ? { status: "valid", email: data.email } : { status: "invalid" });
      } catch {
        if (!cancelled) setState({ status: "invalid" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand-100">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1638780506095-3d61a4f0edd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxvcmdhbml6ZWQlMjBzdG9yYWdlJTIwYm94ZXMlMjBzaGVsZnxlbnwwfHx8fDE3ODE3MjQxNzB8MA&ixlib=rb-4.1.0&q=85"
          alt="Organized storage"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-stoneink-900/55 via-stoneink-900/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-heading text-4xl xl:text-5xl font-semibold leading-tight">A fresh start.<br/>And we&apos;re back home.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-12 w-12 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient">
              <House weight="duotone" size={26} />
            </div>
            <div>
              <div className="font-heading text-2xl font-semibold text-stoneink-900">Nestled</div>
              <div className="text-xs text-stoneink-500">welcome home</div>
            </div>
          </div>

          {state.status === "checking" ? (
            <div className="flex items-center gap-3 text-stoneink-700" data-testid="reset-checking">
              <span className="h-5 w-5 rounded-full border-2 border-terracotta-500 border-t-transparent animate-spin" />
              Checking your link…
            </div>
          ) : state.status === "invalid" ? (
            <div data-testid="reset-invalid">
              <div className="h-14 w-14 rounded-2xl bg-terracotta-50 text-terracotta-600 grid place-items-center">
                <XCircle weight="duotone" size={28} />
              </div>
              <h1 className="mt-5 font-heading text-4xl text-stoneink-900 font-semibold tracking-tight">Link expired</h1>
              <p className="mt-3 text-stoneink-700">This reset link is invalid or has already been used. Request a fresh one.</p>
              <Link to="/forgot-password" data-testid="get-new-link" className="mt-8 inline-flex items-center gap-2 h-12 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium">
                Get a new link
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <div className="h-14 w-14 rounded-2xl bg-sage-400/20 text-sage-600 grid place-items-center">
                <CheckCircle weight="duotone" size={28} />
              </div>
              <h1 className="mt-5 font-heading text-4xl text-stoneink-900 font-semibold tracking-tight">All set.</h1>
              <p className="mt-3 text-stoneink-700">Your password has been updated. Taking you to sign in…</p>
            </div>
          ) : (
            <div data-testid="reset-form-wrap">
              <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Choose a new password</h1>
              <p className="mt-3 text-stoneink-700">For <span className="font-medium text-stoneink-900">{state.email}</span></p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">New password</label>
                  <div className="mt-2 relative">
                    <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
                    <input
                      data-testid="reset-password-input"
                      type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-12 pl-10 pr-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                      placeholder="At least 6 characters"
                      autoFocus
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Confirm</label>
                  <div className="mt-2 relative">
                    <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
                    <input
                      data-testid="reset-confirm-input"
                      type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      className="w-full h-12 pl-10 pr-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                      placeholder="Type it again"
                    />
                  </div>
                </div>
                {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
                <button
                  data-testid="reset-submit-btn"
                  type="submit" disabled={busy}
                  className="w-full h-12 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>

              <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-stoneink-700 hover:text-terracotta-600 text-sm">
                <ArrowLeft size={16} /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
